import React, { useEffect, useState, useCallback } from "react";
import { AdminAPI, BillingAPI } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import AnalyticsTab from "@/components/admin/AnalyticsTab";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "analytics", label: "Analytics" },
  { key: "designers", label: "Designers" },
  { key: "assessments", label: "Assessments" },
  { key: "jobs", label: "Jobs" },
  { key: "approvals", label: "Approvals" },
  { key: "allowlist", label: "Access list" },
  { key: "prompts", label: "Prompts" },
  { key: "billing", label: "Billing" },
  { key: "danger", label: "Danger zone" },
];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="v-container" style={{ paddingTop: 40, paddingBottom: 96 }}>
      <div className="overline" data-testid="admin-overline">Super admin · Verdict</div>
      <h1 className="display" style={{ fontSize: "clamp(36px,4.5vw,56px)", lineHeight: 1.05, marginTop: 12 }}>
        The studio, end to end.
      </h1>

      <div role="tablist" style={{ display: "flex", gap: 6, marginTop: 28, borderBottom: "1px solid var(--v-border)", flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            style={{
              background: "transparent",
              border: "none",
              padding: "12px 16px",
              fontSize: 12,
              letterSpacing: ".15em",
              textTransform: "uppercase",
              cursor: "pointer",
              color: tab === t.key ? "var(--v-text)" : "var(--v-text-2)",
              borderBottom: tab === t.key ? "2px solid var(--v-accent)" : "2px solid transparent",
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 32 }}>
        {tab === "overview" && <OverviewTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "designers" && <DesignersTab />}
        {tab === "assessments" && <AssessmentsTab />}
        {tab === "jobs" && <JobsTab />}
        {tab === "approvals" && <ApprovalsTab />}
        {tab === "allowlist" && <AllowlistTab />}
        {tab === "prompts" && <PromptsTab />}
        {tab === "billing" && <BillingTab />}
        {tab === "danger" && <DangerZoneTab />}
      </div>
    </div>
  );
}

// ----------------------------- Overview -----------------------------
function OverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => { AdminAPI.analytics().then((r) => setData(r.data)); }, []);
  if (!data) return <div className="overline">Loading analytics…</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat l="Projects" v={data.totals.projects} testId="stat-projects" />
        <Stat l="Live" v={data.totals.live_projects} testId="stat-live-projects" />
        <Stat l="Brands" v={data.totals.brands} testId="stat-brands" />
        <Stat l="Assessments" v={data.totals.assessments} testId="stat-assessments" />
        <Stat l="Designers" v={data.totals.designers} testId="stat-designers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" style={{ marginTop: 40 }}>
        <div className="lg:col-span-7 v-surface" style={{ padding: 24 }} data-testid="common-shortcomings">
          <div className="overline">Most common shortcomings</div>
          <h3 style={{ marginTop: 6 }}>Repeating across projects</h3>
          <div style={{ height: 320, marginTop: 16 }}>
            <ResponsiveContainer>
              <BarChart data={data.common_shortcomings} layout="vertical" margin={{ left: 12, right: 24 }}>
                <CartesianGrid stroke="var(--v-border)" strokeDasharray="0" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fill: "var(--v-text-2)", fontSize: 11 }} />
                <YAxis type="category" dataKey="tag" tick={{ fill: "var(--v-text)", fontSize: 11 }} width={180} />
                <Tooltip contentStyle={{ background: "var(--v-surface)", border: "1px solid var(--v-border)" }} />
                <Bar dataKey="count" fill="var(--v-accent)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {data.common_shortcomings.length === 0 && <div className="mono" style={{ color: "var(--v-text-2)", marginTop: 8 }}>No data yet</div>}
        </div>

        <div className="lg:col-span-5 v-surface" style={{ padding: 24 }} data-testid="dimension-averages">
          <div className="overline">Average score per dimension</div>
          <h3 style={{ marginTop: 6 }}>Where the studio is weakest</h3>
          <ul style={{ marginTop: 12, padding: 0, listStyle: "none" }}>
            {data.dimension_averages.map((d) => (
              <li key={d.key} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--v-border)" }}>
                <span style={{ fontSize: 13 }}>{d.label || d.key}</span>
                <span className="mono"><b style={{ color: d.avg < 6 ? "var(--v-accent)" : "var(--v-text)" }}>{d.avg}</b> · n={d.count}</span>
              </li>
            ))}
            {data.dimension_averages.length === 0 && <li className="mono" style={{ color: "var(--v-text-2)" }}>No data yet</li>}
          </ul>
        </div>
      </div>
    </>
  );
}

// ----------------------------- Designers -----------------------------
function DesignersTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null); // user_id being mutated
  const load = useCallback(async () => {
    const { data } = await AdminAPI.designers();
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (u, status) => {
    setBusy(u.user_id);
    try {
      await AdminAPI.patchUser(u.user_id, { status });
      toast.success(`${u.email} marked ${status}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };
  const setRole = async (u, role) => {
    setBusy(u.user_id);
    try {
      await AdminAPI.patchUser(u.user_id, { role });
      toast.success(`${u.email} → ${role}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };
  const hardDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This also removes ${u.projects_count} project(s) and ${u.assessments_count} assessment(s) they own.`)) return;
    setBusy(u.user_id);
    try {
      await AdminAPI.deleteUser(u.user_id);
      toast.success(`${u.email} deleted.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(null); }
  };
  const sendTest = async (u) => {
    setBusy(u.user_id);
    try {
      const { data } = await AdminAPI.sendTestEmail(u.user_id);
      const t = data?.transport || "log_only";
      if (t === "log_only") {
        toast(`Test email logged only — no Gmail/SMTP transport configured yet. Connect Gmail in Settings to enable delivery.`, { duration: 6000 });
      } else {
        toast.success(`Test email sent to ${u.email} via ${t.toUpperCase()}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2 style={{ fontSize: 24 }}>Designers ({rows.length})</h2>
        <a className="v-btn v-btn-outline" href={AdminAPI.designersCsvUrl()} data-testid="download-designers-csv" style={{ marginLeft: "auto" }}>
          Download CSV
        </a>
      </div>

      <div className="v-surface" style={{ marginTop: 16, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="designers-table">
          <thead>
            <tr style={{ textAlign: "left", color: "var(--v-text-2)", fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase" }}>
              <Th>Designer</Th><Th>Role</Th><Th>Status</Th><Th right>Projects</Th><Th right>Assess.</Th><Th>Credits</Th><Th>Last activity</Th><Th right>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} data-testid={`designer-row-${u.user_id}`} style={{ borderTop: "1px solid var(--v-border)" }}>
                <Td>
                  <div style={{ fontWeight: 500 }}>{u.name}</div>
                  <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)" }}>{u.email}</div>
                </Td>
                <Td><Pill text={u.role} accent={u.role === "admin"} /></Td>
                <Td><Pill text={u.status} accent={u.status === "active"} warn={u.status === "pending"} danger={u.status === "revoked"} /></Td>
                <Td right>{u.projects_count}</Td>
                <Td right>{u.assessments_count}</Td>
                <Td>
                  <CreditCell user={u} busy={busy === u.user_id} setBusy={setBusy} onUpdated={load} />
                </Td>
                <Td>{u.last_assessment_at ? new Date(u.last_assessment_at).toLocaleDateString() : "—"}</Td>
                <Td right>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {u.status !== "active" && <SmallBtn onClick={() => setStatus(u, "active")} disabled={busy === u.user_id} testid={`activate-${u.user_id}`}>Activate</SmallBtn>}
                    {u.status === "active" && <SmallBtn onClick={() => setStatus(u, "revoked")} disabled={busy === u.user_id} danger testid={`revoke-${u.user_id}`}>Revoke</SmallBtn>}
                    {u.role !== "admin" && <SmallBtn onClick={() => setRole(u, "admin")} disabled={busy === u.user_id} testid={`promote-${u.user_id}`}>Make admin</SmallBtn>}
                    {u.role === "admin" && <SmallBtn onClick={() => setRole(u, "designer")} disabled={busy === u.user_id} testid={`demote-${u.user_id}`}>Demote</SmallBtn>}
                    <SmallBtn onClick={() => sendTest(u)} disabled={busy === u.user_id} testid={`send-test-email-${u.user_id}`}>Send test</SmallBtn>
                    <SmallBtn onClick={() => hardDelete(u)} disabled={busy === u.user_id} danger testid={`delete-user-${u.user_id}`}>Delete</SmallBtn>
                  </div>
                </Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="8" style={{ padding: 24, color: "var(--v-text-2)" }}>No designers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ----------------------------- Assessments -----------------------------
function AssessmentsTab() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const load = useCallback(async (query) => {
    const { data } = await AdminAPI.assessments(query);
    setList(data);
  }, []);
  useEffect(() => { load(""); }, [load]);

  const onDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? Its comments and email logs are also removed.`)) return;
    setBusy(a.assessment_id);
    try {
      await AdminAPI.deleteAssessment(a.assessment_id);
      toast.success("Assessment deleted");
      await load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 24 }}>All assessments ({list.length})</h2>
        <input
          className="v-input"
          placeholder="Search title…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(q)}
          data-testid="admin-search-input"
          style={{ maxWidth: 280, marginLeft: "auto" }}
        />
        <button className="v-btn v-btn-outline" onClick={() => load(q)} data-testid="admin-search-btn">Search</button>
        <a className="v-btn v-btn-outline" href={AdminAPI.assessmentsCsvUrl()} data-testid="download-assessments-csv">CSV</a>
      </div>

      <div className="grid grid-cols-1 gap-3" style={{ marginTop: 20 }}>
        {list.map((a) => (
          <div key={a.assessment_id} className="v-surface" data-testid={`admin-row-${a.assessment_id}`} style={{ padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <a href={`/assessments/${a.assessment_id}`} style={{ textDecoration: "none", color: "inherit", flex: 1, minWidth: 240 }}>
              <div className="overline">{a.assessment_kind} · {a.model_provider}/{a.model_name}</div>
              <div style={{ marginTop: 4, fontSize: 17 }}>{a.title}</div>
              <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 11, marginTop: 2 }}>
                by {a.designer_name} · {new Date(a.created_at).toLocaleString()}
              </div>
            </a>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="v-score" style={{ fontSize: 28 }}>{Number(a.overall_score || 0).toFixed(1)}</div>
              <a className="v-btn v-btn-outline" href={`/assessments/${a.assessment_id}`} data-testid={`open-${a.assessment_id}`}>Open</a>
              <SmallBtn onClick={() => onDelete(a)} disabled={busy === a.assessment_id} danger testid={`delete-assessment-${a.assessment_id}`}>Delete</SmallBtn>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="mono" style={{ color: "var(--v-text-2)" }}>No assessments yet.</div>}
      </div>
    </div>
  );
}

// ----------------------------- Approvals -----------------------------
function ApprovalsTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const { data } = await AdminAPI.approvals(statusFilter);
    setItems(data);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (it) => {
    setBusy(it.user_id);
    try { await AdminAPI.approve(it.user_id, true); toast.success(`${it.email} approved & allow-listed`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(null); }
  };
  const reject = async (it) => {
    if (!window.confirm(`Reject ${it.email}? They will be signed out and marked as revoked.`)) return;
    setBusy(it.user_id);
    try { await AdminAPI.reject(it.user_id); toast.success(`${it.email} rejected`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(null); }
  };
  const rejectAndDelete = async (it) => {
    if (!window.confirm(`Reject AND permanently delete ${it.email}? This wipes the user record and any projects they own. Cannot be undone.`)) return;
    setBusy(it.user_id);
    try { await AdminAPI.rejectAndDelete(it.user_id); toast.success(`${it.email} rejected & deleted`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject+delete failed"); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <h2 style={{ fontSize: 24 }}>Approval queue ({items.length})</h2>
        <select
          className="v-input"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="approvals-status-filter"
          style={{ marginLeft: "auto", maxWidth: 200 }}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-3" style={{ marginTop: 20 }}>
        {items.map((it) => (
          <div key={it.user_id} className="v-surface" data-testid={`approval-${it.user_id}`} style={{ padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 17, fontWeight: 500 }}>{it.name}</div>
              <div className="mono" style={{ fontSize: 12, color: "var(--v-text-2)" }}>{it.email}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)", marginTop: 4 }}>
                Requested {new Date(it.requested_at).toLocaleString()}
              </div>
            </div>
            {statusFilter === "pending" ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <SmallBtn onClick={() => approve(it)} disabled={busy === it.user_id} testid={`approve-${it.user_id}`}>Approve</SmallBtn>
                <SmallBtn onClick={() => reject(it)} disabled={busy === it.user_id} danger testid={`reject-${it.user_id}`}>Reject</SmallBtn>
                <SmallBtn onClick={() => rejectAndDelete(it)} disabled={busy === it.user_id} danger testid={`reject-delete-${it.user_id}`}>Reject + delete</SmallBtn>
              </div>
            ) : (
              <Pill text={it.status} accent={it.status === "approved"} danger={it.status === "rejected"} />
            )}
          </div>
        ))}
        {items.length === 0 && <div className="mono" style={{ color: "var(--v-text-2)" }}>Nothing in this queue.</div>}
      </div>
    </div>
  );
}

// ----------------------------- Allowlist -----------------------------
function AllowlistTab() {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await AdminAPI.allowlist();
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await AdminAPI.addAllowlist(email.trim().toLowerCase(), note.trim());
      toast.success(`${email} added to access list`);
      setEmail(""); setNote("");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove ${it.email} from the access list? Existing sessions remain — revoke from Designers tab if you want to kick them out.`)) return;
    setBusy(true);
    try { await AdminAPI.removeAllowlist(it.email); toast.success("Removed"); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h2 style={{ fontSize: 24 }}>Access list — outside the studio domain</h2>
      <p style={{ color: "var(--v-text-2)", maxWidth: 640, marginTop: 6, fontSize: 13 }}>
        Anyone at <span className="mono">@thereforedesign.co.in</span> gets designer access automatically.
        Add specific outside emails here to grant the same level of access (create projects + assessments).
      </p>

      <form onSubmit={add} className="v-surface" data-testid="allowlist-form" style={{ padding: 20, marginTop: 20, display: "grid", gridTemplateColumns: "2fr 2fr auto", gap: 12, alignItems: "end" }}>
        <div>
          <div className="overline">Email</div>
          <input
            className="v-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="freelancer@example.com"
            required
            data-testid="allowlist-email-input"
          />
        </div>
        <div>
          <div className="overline">Note (optional)</div>
          <input
            className="v-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Freelance illustrator, Sept '26"
            data-testid="allowlist-note-input"
          />
        </div>
        <button className="v-btn" disabled={busy} type="submit" data-testid="allowlist-add-btn">Add</button>
      </form>

      <div className="v-surface" style={{ marginTop: 16, padding: 0, overflow: "hidden" }} data-testid="allowlist-table">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--v-text-2)", fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase" }}>
              <Th>Email</Th><Th>Note</Th><Th>Added by</Th><Th>When</Th><Th right>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.email} data-testid={`allowlist-row-${it.email}`} style={{ borderTop: "1px solid var(--v-border)" }}>
                <Td><span className="mono">{it.email}</span></Td>
                <Td>{it.note || "—"}</Td>
                <Td className="mono" style={{ fontSize: 12, color: "var(--v-text-2)" }}>{it.added_by}</Td>
                <Td>{it.added_at ? new Date(it.added_at).toLocaleDateString() : "—"}</Td>
                <Td right>
                  <SmallBtn danger onClick={() => remove(it)} testid={`remove-allowlist-${it.email}`} disabled={busy}>Remove</SmallBtn>
                </Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" style={{ padding: 24, color: "var(--v-text-2)" }}>No external emails allow-listed yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ----------------------------- Danger Zone -----------------------------
function DangerZoneTab() {
  const [projectId, setProjectId] = useState("");
  const [assessmentId, setAssessmentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const deleteProject = async () => {
    const id = projectId.trim();
    if (!id) return;
    if (!window.confirm(`Permanently delete project ${id} including all its assessments, assets and comments? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await AdminAPI.deleteProject(id);
      toast.success(`Project deleted (${data.assessments_deleted} assessment[s] removed)`);
      setProjectId("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const deleteAssessment = async () => {
    const id = assessmentId.trim();
    if (!id) return;
    if (!window.confirm(`Permanently delete assessment ${id}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await AdminAPI.deleteAssessment(id);
      toast.success("Assessment deleted");
      setAssessmentId("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const factoryReset = async () => {
    if (resetConfirm !== "FACTORY RESET") return;
    if (!window.confirm("This will WIPE ALL designers, projects, assessments, variation sets, comments, library assets, allowlist, approvals, gmail tokens, usage history and sessions across this app. The super-admin password stays. There is no undo. Proceed?")) return;
    setResetBusy(true);
    setResetResult(null);
    try {
      const { data } = await AdminAPI.factoryReset();
      setResetResult(data);
      toast.success("Factory reset complete. Logging out…");
      // Sessions are gone server-side. Force a sign-in cycle.
      setTimeout(() => { window.location.href = "/"; }, 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Factory reset failed");
    } finally { setResetBusy(false); }
  };

  return (
    <div>
      <h2 style={{ fontSize: 24 }}>Danger zone</h2>
      <p style={{ color: "var(--v-text-2)", maxWidth: 640, marginTop: 6, fontSize: 13 }}>
        Surgical deletes by ID. For routine per-row actions, prefer the Designers and Assessments tabs.
        Everything here cascades and is irreversible.
      </p>

      <div className="v-surface" style={{ padding: 20, marginTop: 20, borderColor: "var(--v-accent)" }} data-testid="danger-project-card">
        <div className="overline">Delete a project</div>
        <p style={{ color: "var(--v-text-2)", fontSize: 12, marginTop: 4 }}>
          Removes the project, all its assessments, assets, comments and collaborator links.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          <input className="v-input" value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="proj_..." data-testid="danger-project-id" style={{ maxWidth: 320 }} />
          <button className="v-btn v-btn-outline" disabled={busy || !projectId.trim()} onClick={deleteProject} data-testid="danger-delete-project-btn">Delete project</button>
        </div>
      </div>

      <div className="v-surface" style={{ padding: 20, marginTop: 16 }} data-testid="danger-assessment-card">
        <div className="overline">Delete an assessment</div>
        <p style={{ color: "var(--v-text-2)", fontSize: 12, marginTop: 4 }}>
          Removes the assessment and its comments + email log entries.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          <input className="v-input" value={assessmentId} onChange={(e) => setAssessmentId(e.target.value)} placeholder="asm_..." data-testid="danger-assessment-id" style={{ maxWidth: 320 }} />
          <button className="v-btn v-btn-outline" disabled={busy || !assessmentId.trim()} onClick={deleteAssessment} data-testid="danger-delete-assessment-btn">Delete assessment</button>
        </div>
      </div>

      <div className="v-surface" style={{ padding: 20, marginTop: 32, borderLeft: "3px solid #ef4444" }} data-testid="danger-factory-reset-card">
        <div className="overline" style={{ color: "#ef4444" }}>Factory reset · NUCLEAR</div>
        <p style={{ color: "var(--v-text-2)", fontSize: 13, marginTop: 6, maxWidth: 720, lineHeight: 1.55 }}>
          Wipes <b>every designer, project, assessment, variation set, comment, library asset, allowlist entry, approval request, Gmail token, usage record and active session</b> in this app's database. The super-admin password and any custom LLM prompts you've saved are preserved. Use only to start a fresh production rollout.
        </p>
        <p style={{ color: "var(--v-text-2)", fontSize: 12, marginTop: 10 }}>
          Type <b>FACTORY RESET</b> exactly to enable the button.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
          <input
            className="v-input"
            value={resetConfirm}
            onChange={(e) => setResetConfirm(e.target.value)}
            placeholder="FACTORY RESET"
            data-testid="danger-factory-reset-input"
            style={{ maxWidth: 240 }}
          />
          <button
            className="v-btn v-btn-outline"
            disabled={resetBusy || resetConfirm !== "FACTORY RESET"}
            onClick={factoryReset}
            data-testid="danger-factory-reset-btn"
            style={{ borderColor: "#ef4444", color: "#ef4444" }}
          >
            {resetBusy ? "Wiping…" : "Factory reset everything"}
          </button>
        </div>
        {resetResult && (
          <pre data-testid="danger-factory-reset-result" style={{ marginTop: 12, fontSize: 11, padding: 12, background: "var(--v-bg)", border: "1px solid var(--v-border)", overflow: "auto" }}>
            {JSON.stringify(resetResult.deleted_counts, null, 2)}
          </pre>
        )}
      </div>

      <p style={{ color: "var(--v-text-2)", fontSize: 12, marginTop: 24 }}>
        Need to delete a designer or revoke their access? Use the <b>Designers</b> tab — it's safer
        because it shows what'll be cascaded.
      </p>
    </div>
  );
}

// ----------------------------- Prompts -----------------------------
function PromptsTab() {
  const [data, setData] = useState(null);
  const [dims, setDims] = useState([]);
  const [systemDims, setSystemDims] = useState([]);
  const [synthesisTone, setSynthesisTone] = useState("");
  const [refreshTone, setRefreshTone] = useState("");
  const [systemSynthesisTone, setSystemSynthesisTone] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeSet, setActiveSet] = useState("pack"); // "pack" | "system"

  const load = useCallback(async () => {
    const { data } = await AdminAPI.getPrompts();
    setData(data);
    setDims(data.dimensions);
    setSystemDims(data.system_dimensions || []);
    setSynthesisTone(data.synthesis_tone || "");
    setRefreshTone(data.refresh_tone || "");
    setSystemSynthesisTone(data.system_synthesis_tone || "");
  }, []);
  useEffect(() => { load(); }, [load]);

  const currentDims = activeSet === "pack" ? dims : systemDims;
  const setCurrentDims = activeSet === "pack" ? setDims : setSystemDims;

  const setDim = (idx, key, value) => {
    setCurrentDims((prev) => prev.map((d, i) => (i === idx ? { ...d, [key]: value } : d)));
  };

  const save = async () => {
    setBusy(true);
    try {
      await AdminAPI.patchPrompts({
        dimensions: dims,
        system_dimensions: systemDims,
        synthesis_tone: synthesisTone,
        refresh_tone: refreshTone,
        system_synthesis_tone: systemSynthesisTone,
      });
      toast.success("Prompts saved. Next assessment will use them immediately.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const resetField = async (field) => {
    if (!window.confirm(`Reset ${field} to the studio default?`)) return;
    setBusy(true);
    try {
      await AdminAPI.resetPrompts([field]);
      toast.success(`Reset ${field} to default.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const resetSingleDim = (idx) => {
    const defaultsList = activeSet === "pack" ? data.defaults.dimensions : data.defaults.system_dimensions;
    const key = currentDims[idx].key;
    const d = defaultsList.find((x) => x.key === key);
    if (!d) return;
    setCurrentDims((prev) => prev.map((p, i) => (i === idx ? { ...d } : p)));
    toast.message(`Loaded default for ${d.label}. Click "Save changes" to apply.`);
  };

  if (!data) return <div className="overline">Loading prompts…</div>;

  const setLabel = activeSet === "pack" ? "Pack audit" : "System architecture";
  const dimsFieldName = activeSet === "pack" ? "dimensions" : "system_dimensions";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 24 }}>LLM prompts</h2>
        <p style={{ color: "var(--v-text-2)", fontSize: 13, maxWidth: 640 }}>
          Two independent rubrics — one for single-SKU pack audits, one for system-level architecture audits.
          Changes take effect on the very next assessment. Use <span className="mono">Reset</span> to fall back to defaults.
        </p>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="v-btn v-btn-outline" onClick={() => resetField(dimsFieldName)} disabled={busy} data-testid={`prompts-reset-${dimsFieldName}`}>Reset all {setLabel.toLowerCase()} dimensions</button>
          <button className="v-btn" onClick={save} disabled={busy} data-testid="prompts-save-btn">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>

      {/* Pack vs System toggle */}
      <div role="tablist" data-testid="prompts-set-toggle" style={{ marginTop: 20, display: "inline-flex", border: "1px solid var(--v-border)", borderRadius: 999, overflow: "hidden" }}>
        {[
          { key: "pack", label: `Pack audit · ${dims.length} dimensions` },
          { key: "system", label: `System architecture · ${systemDims.length} dimensions` },
        ].map((t) => (
          <button
            key={t.key}
            role="tab"
            data-testid={`prompts-set-${t.key}`}
            onClick={() => setActiveSet(t.key)}
            style={{
              padding: "8px 18px", fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase",
              border: "none",
              background: activeSet === t.key ? "var(--v-accent)" : "transparent",
              color: activeSet === t.key ? "#000" : "var(--v-text-2)",
              cursor: "pointer", fontWeight: 600,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 24, display: "grid", gap: 14 }}>
        {currentDims.map((d, i) => (
          <div key={`${activeSet}-${d.key}`} className="v-surface" style={{ padding: 20 }} data-testid={`prompt-dim-${d.key}`}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)" }}>{String(i + 1).padStart(2, "0")}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)" }}>{d.key}</div>
              <button
                type="button"
                onClick={() => resetSingleDim(i)}
                disabled={busy}
                style={{ marginLeft: "auto", background: "transparent", border: "none", color: "var(--v-accent)", fontSize: 11, cursor: "pointer", letterSpacing: ".06em", textTransform: "uppercase" }}
                data-testid={`prompt-dim-reset-${d.key}`}
              >
                Load default
              </button>
            </div>
            <div style={{ marginTop: 8 }}>
              <div className="overline">Label (shown in the report)</div>
              <input
                className="v-input"
                value={d.label || ""}
                onChange={(e) => setDim(i, "label", e.target.value)}
                data-testid={`prompt-dim-label-${d.key}`}
              />
            </div>
            <div style={{ marginTop: 10 }}>
              <div className="overline">Guidance (what to weigh in this dimension)</div>
              <textarea
                className="v-textarea"
                rows={3}
                value={d.guidance || ""}
                onChange={(e) => setDim(i, "guidance", e.target.value)}
                data-testid={`prompt-dim-guidance-${d.key}`}
                style={{ minHeight: 80, fontSize: 13, lineHeight: 1.55 }}
              />
            </div>
          </div>
        ))}

        {activeSet === "pack" && (
          <>
            <div className="v-surface" style={{ padding: 20 }} data-testid="prompt-synthesis-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div className="overline">Synthesis tone · pack audit</div>
                  <h3 style={{ marginTop: 4, fontSize: 18 }}>The final-verdict voice</h3>
                </div>
                <button type="button" onClick={() => resetField("synthesis_tone")} disabled={busy}
                  style={{ background: "transparent", border: "none", color: "var(--v-accent)", fontSize: 11, cursor: "pointer", letterSpacing: ".06em", textTransform: "uppercase" }}
                  data-testid="prompt-synthesis-reset">Reset</button>
              </div>
              <textarea className="v-textarea" rows={3} value={synthesisTone} onChange={(e) => setSynthesisTone(e.target.value)}
                data-testid="prompt-synthesis-tone" style={{ marginTop: 10, minHeight: 80, fontSize: 13, lineHeight: 1.55 }} />
            </div>

            <div className="v-surface" style={{ padding: 20 }} data-testid="prompt-refresh-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div className="overline">Refresh-comparison tone</div>
                  <h3 style={{ marginTop: 4, fontSize: 18 }}>How the existing-pack delta is critiqued</h3>
                </div>
                <button type="button" onClick={() => resetField("refresh_tone")} disabled={busy}
                  style={{ background: "transparent", border: "none", color: "var(--v-accent)", fontSize: 11, cursor: "pointer", letterSpacing: ".06em", textTransform: "uppercase" }}
                  data-testid="prompt-refresh-reset">Reset</button>
              </div>
              <textarea className="v-textarea" rows={3} value={refreshTone} onChange={(e) => setRefreshTone(e.target.value)}
                data-testid="prompt-refresh-tone" style={{ marginTop: 10, minHeight: 80, fontSize: 13, lineHeight: 1.55 }} />
            </div>
          </>
        )}

        {activeSet === "system" && (
          <div className="v-surface" style={{ padding: 20 }} data-testid="prompt-system-synthesis-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div className="overline">Synthesis tone · system architecture</div>
                <h3 style={{ marginTop: 4, fontSize: 18 }}>How the architecture verdict is written</h3>
              </div>
              <button type="button" onClick={() => resetField("system_synthesis_tone")} disabled={busy}
                style={{ background: "transparent", border: "none", color: "var(--v-accent)", fontSize: 11, cursor: "pointer", letterSpacing: ".06em", textTransform: "uppercase" }}
                data-testid="prompt-system-synthesis-reset">Reset</button>
            </div>
            <textarea className="v-textarea" rows={3} value={systemSynthesisTone} onChange={(e) => setSystemSynthesisTone(e.target.value)}
              data-testid="prompt-system-synthesis-tone" style={{ marginTop: 10, minHeight: 80, fontSize: 13, lineHeight: 1.55 }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------- Tiny UI helpers -----------------------------
function Stat({ l, v, testId }) {
  return (
    <div className="v-surface" style={{ padding: 18 }} data-testid={testId}>
      <div className="overline">{l}</div>
      <div className="v-score" style={{ fontSize: 40, marginTop: 6 }}>{v}</div>
    </div>
  );
}
function Th({ children, right }) {
  return <th style={{ padding: "12px 16px", textAlign: right ? "right" : "left", fontWeight: 500 }}>{children}</th>;
}
function Td({ children, right, ...rest }) {
  return <td {...rest} style={{ padding: "14px 16px", textAlign: right ? "right" : "left", verticalAlign: "middle", ...(rest.style || {}) }}>{children}</td>;
}
function Pill({ text, accent, warn, danger }) {
  const bg = danger ? "rgba(220,38,38,0.12)" : warn ? "rgba(234,88,12,0.14)" : accent ? "var(--v-accent)" : "var(--v-bg-2, rgba(255,255,255,0.06))";
  const fg = danger ? "#dc2626" : warn ? "#ea580c" : accent ? "#000" : "var(--v-text)";
  return <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", background: bg, color: fg, fontWeight: 600 }}>{text}</span>;
}

function CreditCell({ user, busy, setBusy, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(user.credits_cap ?? 5));
  useEffect(() => { setDraft(String(user.credits_cap ?? 5)); }, [user.credits_cap]);

  const save = async () => {
    const cap = parseFloat(draft);
    if (!Number.isFinite(cap) || cap < 0) {
      toast.error("Cap must be a number ≥ 0");
      return;
    }
    setBusy(user.user_id);
    try {
      await AdminAPI.setDesignerCap(user.user_id, cap);
      toast.success(`${user.email}: cap set to ${cap} cr`);
      setEditing(false);
      await onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };
  const reset = async () => {
    if (!window.confirm(`Reset ${user.email}'s current-month usage to 0?`)) return;
    setBusy(user.user_id);
    try {
      await AdminAPI.resetDesignerUsage(user.user_id);
      toast.success("Usage reset to 0 cr");
      await onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(null); }
  };

  const used = Number(user.credits_used || 0);
  const cap = Number(user.credits_cap || 0);
  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
  const barColor = pct >= 100 ? "#dc2626" : pct >= 80 ? "#F59E0B" : "var(--v-accent)";

  return (
    <div style={{ minWidth: 160 }} data-testid={`credit-cell-${user.user_id}`}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontWeight: 700, color: pct >= 100 ? "#dc2626" : "var(--v-text)" }} data-testid={`credit-used-${user.user_id}`}>
          {used.toFixed(2)}
        </span>
        <span style={{ color: "var(--v-text-2)", fontSize: 12 }}>/</span>
        {editing ? (
          <input
            className="v-input"
            type="number" step="0.5" min="0"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            data-testid={`credit-cap-input-${user.user_id}`}
            style={{ width: 64, padding: "2px 6px", fontSize: 12, height: 24 }}
          />
        ) : (
          <button
            onClick={() => setEditing(true)}
            disabled={busy}
            data-testid={`credit-cap-edit-${user.user_id}`}
            style={{ background: "transparent", border: "none", color: "var(--v-text)", cursor: "pointer", padding: 0, fontWeight: 600, fontSize: 14, borderBottom: "1px dashed var(--v-text-2)" }}
            title="Click to edit monthly cap"
          >
            {cap.toFixed(2)} cr
          </button>
        )}
      </div>
      <div style={{ height: 3, background: "var(--v-border)", marginTop: 6, borderRadius: 1 }}>
        <div style={{ width: `${pct}%`, height: 3, background: barColor, transition: "width .3s ease" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
        <div className="mono" style={{ fontSize: 9, color: "var(--v-text-2)", letterSpacing: ".06em" }}>
          {pct >= 100 ? "EXHAUSTED" : pct >= 80 ? `${Math.round(pct)}% USED` : `${user.credits_period || ""}`}
        </div>
        {used > 0 && (
          <button
            onClick={reset}
            disabled={busy}
            data-testid={`credit-reset-${user.user_id}`}
            style={{ background: "transparent", border: "none", color: "var(--v-text-2)", fontSize: 9, cursor: "pointer", padding: 0, letterSpacing: ".06em", textTransform: "uppercase" }}
            title="Reset current-month usage to 0"
          >
            ↺ reset
          </button>
        )}
      </div>
    </div>
  );
}

function SmallBtn({ children, danger, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        padding: "6px 10px",
        fontSize: 11,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        background: "transparent",
        border: `1px solid ${danger ? "#dc2626" : "var(--v-border)"}`,
        color: danger ? "#dc2626" : "var(--v-text)",
        borderRadius: 4,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ----------------------------- Billing tab -----------------------------
function inrFmt(paise) { return `₹${(paise/100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`; }

function BillingTab() {
  const [bundles, setBundles] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ bundle_id: "", name: "", credits: 1, amount_paise: 10000, blurb: "", is_active: true, sort_order: 50 });

  const load = useCallback(async () => {
    try {
      const [{ data: b }, { data: r }] = await Promise.all([
        BillingAPI.adminBundles(),
        BillingAPI.adminRevenue(),
      ]);
      setBundles(b);
      setRevenue(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (b) => { setEditingId(b.bundle_id); setForm({ ...b }); };
  const cancelEdit = () => { setEditingId(null); setForm({ bundle_id: "", name: "", credits: 1, amount_paise: 10000, blurb: "", is_active: true, sort_order: 50 }); };

  const save = async () => {
    setBusy(true);
    try {
      if (editingId) {
        await BillingAPI.adminUpdateBundle(editingId, {
          name: form.name, credits: Number(form.credits), amount_paise: Number(form.amount_paise),
          blurb: form.blurb, is_active: !!form.is_active, sort_order: Number(form.sort_order),
        });
        toast.success("Bundle updated");
      } else {
        await BillingAPI.adminCreateBundle({
          bundle_id: form.bundle_id.trim(), name: form.name, credits: Number(form.credits),
          amount_paise: Number(form.amount_paise), blurb: form.blurb, is_active: !!form.is_active,
          sort_order: Number(form.sort_order),
        });
        toast.success("Bundle created");
      }
      cancelEdit();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete bundle "${b.name}"? This won't refund any past purchases.`)) return;
    await BillingAPI.adminDeleteBundle(b.bundle_id);
    toast.success("Bundle deleted"); await load();
  };

  return (
    <div data-testid="admin-billing-tab">
      <h2 style={{ fontSize: 24 }}>Billing & pricing</h2>
      <p style={{ color: "var(--v-text-2)", fontSize: 13, marginTop: 6, maxWidth: 660 }}>
        Edit your audit-credit bundles below. Changes are live immediately on
        <code>/pricing</code> — no redeploy needed. Internal designers
        (@thereforedesign.co.in) are never charged.
      </p>

      {revenue && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 24 }}>
          <BillingStat label="Lifetime revenue" value={inrFmt(revenue.totals.total_paise || 0)} />
          <BillingStat label="Top-ups completed" value={revenue.totals.topup_count || 0} />
          <BillingStat label="External customers" value={revenue.customers?.length || 0} />
          <BillingStat label="Credits sold" value={revenue.totals.total_credits || 0} />
        </div>
      )}

      <h3 style={{ marginTop: 36, fontSize: 18 }}>Pricing bundles</h3>
      <div style={{ marginTop: 12, border: "1px solid var(--v-border)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="admin-bundles-table">
          <thead style={{ background: "var(--v-bg-2)" }}>
            <tr>
              <Th>ID</Th><Th>Name</Th><Th>Credits</Th><Th>Price</Th><Th>Per audit</Th><Th>Active</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {bundles.map((b) => (
              <tr key={b.bundle_id} style={{ borderTop: "1px solid var(--v-border)" }} data-testid={`bundle-row-${b.bundle_id}`}>
                <Td><code>{b.bundle_id}</code></Td>
                <Td>{b.name}</Td>
                <Td>{b.credits}</Td>
                <Td>{inrFmt(b.amount_paise)}</Td>
                <Td>{inrFmt(Math.round(b.amount_paise / b.credits))}</Td>
                <Td>{b.is_active ? "Yes" : "No"}</Td>
                <Td>
                  <button className="v-btn v-btn-ghost" style={{ padding: "4px 10px", fontSize: 10 }} onClick={() => startEdit(b)}>Edit</button>
                  <button className="v-btn v-btn-ghost" style={{ padding: "4px 10px", fontSize: 10, marginLeft: 6, color: "#ef4444" }} onClick={() => remove(b)}>Delete</button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: 36, fontSize: 18 }}>{editingId ? `Edit "${editingId}"` : "Add a new bundle"}</h3>
      <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, maxWidth: 800 }}>
        {!editingId && <InputBlock label="Bundle ID (slug)" value={form.bundle_id} onChange={(v) => setForm({ ...form, bundle_id: v })} placeholder="growth" testid="bundle-id-input" />}
        <InputBlock label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Growth" testid="bundle-name-input" />
        <InputBlock label="Credits" type="number" value={form.credits} onChange={(v) => setForm({ ...form, credits: v })} testid="bundle-credits-input" />
        <InputBlock label="Price (₹)" type="number" value={Math.round((form.amount_paise || 0)/100)} onChange={(v) => setForm({ ...form, amount_paise: Math.round(Number(v) * 100) })} testid="bundle-price-input" />
        <InputBlock label="Sort order" type="number" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: v })} testid="bundle-sort-input" />
        <label style={{ fontSize: 11, color: "var(--v-text-2)", display: "flex", alignItems: "center", gap: 6, paddingTop: 22 }}>
          <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="bundle-active-input" />
          Active (visible on /pricing)
        </label>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: 10, color: "var(--v-text-2)", textTransform: "uppercase", letterSpacing: ".12em" }}>Blurb</label>
          <textarea className="v-input" rows={2} value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} data-testid="bundle-blurb-input" style={{ width: "100%", marginTop: 4 }} />
        </div>
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
        <button className="v-btn" disabled={busy || !form.name || !form.credits || !form.amount_paise} onClick={save} data-testid="bundle-save-btn">
          {busy ? "Saving…" : editingId ? "Save changes" : "Create bundle"}
        </button>
        {editingId && <button className="v-btn v-btn-ghost" onClick={cancelEdit} data-testid="bundle-cancel-btn">Cancel</button>}
      </div>

      <h3 style={{ marginTop: 48, fontSize: 18 }}>External customers ({revenue?.customers?.length || 0})</h3>
      <div style={{ marginTop: 12, border: "1px solid var(--v-border)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="admin-customers-table">
          <thead style={{ background: "var(--v-bg-2)" }}>
            <tr><Th>Name</Th><Th>Email</Th><Th>Balance</Th><Th>Lifetime credits</Th><Th>Lifetime spend</Th><Th>Joined</Th></tr>
          </thead>
          <tbody>
            {(revenue?.customers || []).map((c) => (
              <tr key={c.user_id} style={{ borderTop: "1px solid var(--v-border)" }} data-testid={`customer-row-${c.user_id}`}>
                <Td>{c.name}</Td><Td className="mono">{c.email}</Td>
                <Td>{c.audit_credits}</Td>
                <Td>{c.lifetime_credits_purchased}</Td>
                <Td>{inrFmt(c.lifetime_spend_inr_paise || 0)}</Td>
                <Td>{new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</Td>
              </tr>
            ))}
            {(!revenue?.customers || revenue.customers.length === 0) && (
              <tr><Td colSpan={6} style={{ color: "var(--v-text-2)", textAlign: "center", padding: 24 }}>No external customers yet.</Td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BillingStat({ label, value }) {
  return (
    <div className="v-surface" style={{ padding: "14px 18px", minWidth: 160 }}>
      <div style={{ fontSize: 10, color: "var(--v-text-2)", textTransform: "uppercase", letterSpacing: ".12em" }}>{label}</div>
      <div style={{ fontSize: 22, marginTop: 4, fontWeight: 300 }}>{value}</div>
    </div>
  );
}
function BillingTh({ children }) { return <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: ".12em", color: "var(--v-text-2)" }}>{children}</th>; }
function BillingTd({ children, ...p }) { return <td style={{ padding: "10px 12px" }} {...p}>{children}</td>; }
function InputBlock({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label style={{ fontSize: 10, color: "var(--v-text-2)", textTransform: "uppercase", letterSpacing: ".12em" }}>{label}</label>
      <input
        className="v-input" type={type} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        style={{ width: "100%", marginTop: 4 }}
      />
    </div>
  );
}


// ----------------------------- Jobs (Batch B) -----------------------------
function JobsTab() {
  const [summary, setSummary] = useState(null);
  const [emails, setEmails] = useState([]);
  const [audits, setAudits] = useState({ assessments: [], variation_sets: [] });
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, e, a] = await Promise.all([
        AdminAPI.jobsSummary(),
        AdminAPI.jobsEmails(statusFilter || undefined, 100),
        AdminAPI.jobsAudits(),
      ]);
      setSummary(s.data);
      setEmails(e.data.items || []);
      setAudits(a.data || { assessments: [], variation_sets: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load jobs");
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 20s so a super admin can watch the queue drain live.
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const retry = async (jobId) => {
    setBusy(jobId);
    try {
      await AdminAPI.jobsRetryEmail(jobId);
      toast.success("Requeued — the worker will retry within a minute.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(null);
    }
  };

  if (!summary) return <div className="overline">Loading jobs…</div>;

  const em = summary.emails || {};
  const asmt = summary.assessments || {};
  const vset = summary.variation_sets || {};

  return (
    <div data-testid="admin-jobs-tab">
      <div className="overline">Emails</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ marginTop: 8 }}>
        <Stat l="Queued" v={em.queued ?? 0} testId="jobs-emails-queued" />
        <Stat l="Sending" v={em.sending ?? 0} testId="jobs-emails-sending" />
        <Stat l="Sent" v={em.sent ?? 0} testId="jobs-emails-sent" />
        <Stat l="Dead" v={em.dead ?? 0} testId="jobs-emails-dead" />
      </div>

      <div className="overline" style={{ marginTop: 32 }}>Audit pipelines</div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4" style={{ marginTop: 8 }}>
        <Stat l="Audits processing" v={asmt.processing ?? 0} testId="jobs-asmt-processing" />
        <Stat l="Audits complete" v={asmt.complete ?? 0} testId="jobs-asmt-complete" />
        <Stat l="Audits failed" v={asmt.failed ?? 0} testId="jobs-asmt-failed" />
        <Stat l="Vset processing" v={vset.processing ?? 0} testId="jobs-vset-processing" />
        <Stat l="Vset complete" v={vset.complete ?? 0} testId="jobs-vset-complete" />
        <Stat l="Vset failed" v={vset.failed ?? 0} testId="jobs-vset-failed" />
      </div>

      {/* --- Email jobs list --- */}
      <div style={{ marginTop: 40, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="overline">Recent email jobs</div>
          <h3 style={{ marginTop: 6 }}>Delivery queue</h3>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            className="v-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="jobs-emails-status-filter"
            style={{ padding: "8px 12px" }}
          >
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="sending">Sending</option>
            <option value="sent">Sent</option>
            <option value="dead">Dead</option>
          </select>
          <button className="v-btn" onClick={load} data-testid="jobs-emails-refresh">Refresh</button>
        </div>
      </div>

      <div className="v-surface" style={{ padding: 0, marginTop: 16, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "var(--v-bg-2, rgba(255,255,255,0.03))", color: "var(--v-text-2)" }}>
            <tr>
              <Th>Kind</Th>
              <Th>To</Th>
              <Th>Status</Th>
              <Th right>Attempts</Th>
              <Th>Next attempt</Th>
              <Th>Sent via</Th>
              <Th>Last error</Th>
              <Th right>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {emails.length === 0 && (
              <tr><Td colSpan={8} style={{ textAlign: "center", color: "var(--v-text-2)", padding: 32 }} data-testid="jobs-emails-empty">No jobs match this filter.</Td></tr>
            )}
            {emails.map((j) => {
              const isDead = j.status === "dead";
              const isQueued = j.status === "queued";
              const isSent = j.status === "sent";
              return (
                <React.Fragment key={j.job_id}>
                  <tr
                    style={{ borderTop: "1px solid var(--v-border)", cursor: j.last_error ? "pointer" : "default" }}
                    onClick={() => j.last_error && setExpandedId(expandedId === j.job_id ? null : j.job_id)}
                    data-testid={`jobs-emails-row-${j.job_id}`}
                  >
                    <Td>{j.kind || "—"}</Td>
                    <Td>{j.to_email}</Td>
                    <Td>
                      <Pill
                        text={j.status}
                        accent={isSent}
                        warn={isQueued || j.status === "sending"}
                        danger={isDead}
                      />
                    </Td>
                    <Td right>{j.attempts ?? 0}<span style={{ color: "var(--v-text-2)" }}> / {j.max_attempts ?? 6}</span></Td>
                    <Td style={{ color: "var(--v-text-2)" }}>{j.next_attempt_at ? new Date(j.next_attempt_at).toLocaleString() : "—"}</Td>
                    <Td>{j.sent_via || "—"}</Td>
                    <Td style={{ maxWidth: 240, color: "var(--v-text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.last_error || "—"}</Td>
                    <Td right>
                      {(isDead || (isQueued && (j.attempts ?? 0) > 0)) && (
                        <button
                          className="v-btn"
                          disabled={busy === j.job_id}
                          onClick={(e) => { e.stopPropagation(); retry(j.job_id); }}
                          data-testid={`jobs-emails-retry-${j.job_id}`}
                          style={{ fontSize: 12, padding: "6px 12px" }}
                        >
                          {busy === j.job_id ? "…" : "Retry"}
                        </button>
                      )}
                    </Td>
                  </tr>
                  {expandedId === j.job_id && j.last_error && (
                    <tr>
                      <Td colSpan={8} style={{ background: "var(--v-bg-2, rgba(255,255,255,0.03))", fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", color: "var(--v-text-2)" }}>
                        {j.last_error}
                      </Td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* --- Stuck audits --- */}
      <div className="overline" style={{ marginTop: 40 }}>Stuck / failed audits</div>
      <h3 style={{ marginTop: 6 }}>Assessments</h3>
      <div className="v-surface" style={{ padding: 0, marginTop: 12, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "var(--v-bg-2, rgba(255,255,255,0.03))", color: "var(--v-text-2)" }}>
            <tr>
              <Th>Brand</Th>
              <Th>Title</Th>
              <Th>Kind</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Error</Th>
            </tr>
          </thead>
          <tbody>
            {audits.assessments.length === 0 && (
              <tr><Td colSpan={6} style={{ textAlign: "center", color: "var(--v-text-2)", padding: 24 }} data-testid="jobs-audits-empty">No stuck audits — the pipeline is clean.</Td></tr>
            )}
            {audits.assessments.map((a) => (
              <tr key={a.assessment_id} style={{ borderTop: "1px solid var(--v-border)" }} data-testid={`jobs-asmt-row-${a.assessment_id}`}>
                <Td>{a.brand_name || "—"}</Td>
                <Td>{a.title || "—"}</Td>
                <Td>{a.assessment_kind || "primary"}</Td>
                <Td><Pill text={a.status} warn={a.status === "processing"} danger={a.status === "failed"} /></Td>
                <Td style={{ color: "var(--v-text-2)" }}>{a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</Td>
                <Td style={{ maxWidth: 280, color: "var(--v-text-2)" }}>{a.error_message || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {audits.variation_sets.length > 0 && (
        <>
          <h3 style={{ marginTop: 32 }}>Variation sets</h3>
          <div className="v-surface" style={{ padding: 0, marginTop: 12, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ background: "var(--v-bg-2, rgba(255,255,255,0.03))", color: "var(--v-text-2)" }}>
                <tr>
                  <Th>Brand</Th>
                  <Th>Title</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th>Error</Th>
                </tr>
              </thead>
              <tbody>
                {audits.variation_sets.map((v) => (
                  <tr key={v.variation_set_id} style={{ borderTop: "1px solid var(--v-border)" }} data-testid={`jobs-vset-row-${v.variation_set_id}`}>
                    <Td>{v.brand_name || "—"}</Td>
                    <Td>{v.title || "—"}</Td>
                    <Td><Pill text={v.status} warn={v.status === "processing"} danger={v.status === "failed"} /></Td>
                    <Td style={{ color: "var(--v-text-2)" }}>{v.created_at ? new Date(v.created_at).toLocaleString() : "—"}</Td>
                    <Td style={{ maxWidth: 280, color: "var(--v-text-2)" }}>{v.error_message || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p style={{ marginTop: 24, fontSize: 12, color: "var(--v-text-2)", lineHeight: 1.6 }}>
        The email worker ticks every 30 seconds. Failed sends retry with exponential backoff
        (1 min → 5 min → 30 min → 2 h → 12 h) before flipping to <b>dead</b>. Stuck audits older
        than 15 min are auto-resumed on backend restart.
      </p>
    </div>
  );
}
